import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DeclarationTypes } from 'store/declarations/enums/common/declaration-types';
import { SelectOption } from 'components/new-ui/composed/declarations/formSelect/DeclarationSelect';
import { ICS2CodelistsMap } from 'store/codelists/code';
import useICS2Codelists from 'hooks/useICS2Codelists';

type Props = {
    fetchOnMount: boolean;
    codelistName: string | undefined;
    declarationType: DeclarationTypes.ICS2 | undefined;
    query: string | undefined;
    // Pre-filled field value used to resolve the selected option's label on mount,
    // before the user has interacted with the dropdown.
    seedQuery?: string;
    countryCode?: string;
};

const useIcs2CodelistOptions = ({
    codelistName,
    declarationType,
    fetchOnMount,
    query,
    seedQuery,
    countryCode,
}: Props) => {
    const { getICS2Codelist } = useICS2Codelists();
    const [loading, setLoading] = useState(false);
    const [codelistOptions, setCodelistOptions] = useState<SelectOption[]>([]);
    const [seedOptions, setSeedOptions] = useState<SelectOption[]>([]);
    const requestIdRef = useRef(0);

    // CL244 (UN/LOCODE) is server-filtered; all other codelists are fetched in full and filtered client-side.
    const isServerFilteredCodelist = codelistName === ICS2CodelistsMap.CL244;

    const fetchCodelist = useCallback(
        async (overrideQuery?: string) => {
            if (!codelistName || !declarationType) {
                setCodelistOptions([]);
                setLoading(false);
                return [];
            }

            if (declarationType !== DeclarationTypes.ICS2) {
                console.error('This functionality is implemented only for ICS2 codelists');
                setCodelistOptions([]);
                setLoading(false);
                return [];
            }

            const currentRequestId = ++requestIdRef.current;
            const effectiveQuery = overrideQuery ?? query;

            setLoading(true);

            try {
                const response = await getICS2Codelist(codelistName, effectiveQuery, countryCode);

                // Ignore stale responses (a newer request is already in flight)
                if (currentRequestId !== requestIdRef.current) {
                    return [];
                }

                setCodelistOptions(response ?? []);
                return response ?? [];
            } catch (err) {
                console.error(err);

                if (currentRequestId !== requestIdRef.current) {
                    return [];
                }

                // For server-filtered codelists, keep the existing options on error so the
                // user's current selection remains visible. For full-fetch codelists, clear
                // to avoid showing a stale list.
                if (!isServerFilteredCodelist) {
                    setCodelistOptions([]);
                }

                return [];
            } finally {
                if (currentRequestId === requestIdRef.current) {
                    setLoading(false);
                }
            }
        },
        [codelistName, declarationType, isServerFilteredCodelist, getICS2Codelist, query, countryCode]
    );

    // Fetch the seed option to resolve the pre-filled value's label on mount (CL244 only).
    // This runs independently of the main search flow so the label is shown before the user
    // opens the dropdown. We deliberately depend only on seedQuery: codelistName and
    // declarationType are stable for the lifetime of a given field.
    useEffect(() => {
        if (!isServerFilteredCodelist || !seedQuery || !codelistName || !declarationType) return;

        getICS2Codelist(codelistName, seedQuery, countryCode)
            .then((response) => setSeedOptions(response ?? []))
            .catch(() => setSeedOptions([]));
    }, [seedQuery]); // eslint-disable-line react-hooks/exhaustive-deps

    // For full-fetch codelists, load all options once when the field is mounted with a value.
    // Server-filtered codelists are driven by the query effect below instead.
    useEffect(() => {
        if (!fetchOnMount || isServerFilteredCodelist) return;

        void fetchCodelist();
    }, [fetchOnMount, isServerFilteredCodelist, fetchCodelist]);

    // For server-filtered codelists, re-fetch whenever the debounced search query changes.
    useEffect(() => {
        if (!isServerFilteredCodelist || query === undefined) return;

        void fetchCodelist(query);
    }, [isServerFilteredCodelist, query, fetchCodelist]);

    // Merge seed options with search results. Seed entries that already appear in the search
    // results are dropped to avoid duplicates; the rest are prepended so the selected value
    // is always visible at the top.
    const mergedOptions = useMemo(() => {
        if (!isServerFilteredCodelist || seedOptions.length === 0) return codelistOptions;

        const searchResultIds = new Set(codelistOptions.map((o) => o.id));
        const seedOnlyOptions = seedOptions.filter((o) => !searchResultIds.has(o.id));
        return [...seedOnlyOptions, ...codelistOptions];
    }, [isServerFilteredCodelist, codelistOptions, seedOptions]);

    return {
        codelistOptions: mergedOptions,
        loading,
        fetchCodelist,
    };
};

export default useIcs2CodelistOptions;
